'use client';

import { ShieldIcon, CalendarDotsIcon } from '@phosphor-icons/react';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { GoalSummary } from '@/types';
import { goalStatus, projectedMonthsToTarget } from './helpers';

interface Props {
  goal: GoalSummary;
  essentialMonthly: number;
  averageMonthlyExpense: number;
  onEdit: () => void;
}

export function ReservaCard({ goal, essentialMonthly, averageMonthlyExpense, onEdit }: Props) {
  const formatCurrency = useFormatCurrency();
  const saved = goal.currentAmount;
  const target = goal.targetAmount;

  // Use average monthly expense (actual spend) to compute how many months the
  // user is covered. Fall back to essentialMonthly if the average is 0 (no
  // transaction history yet).
  const monthlyBase = averageMonthlyExpense > 0 ? averageMonthlyExpense : essentialMonthly;
  const monthsCovered = monthlyBase > 0 ? saved / monthlyBase : 0;

  const p = target > 0 ? Math.min(999, Math.round((saved / target) * 100)) : 0;
  const status = goalStatus(goal);
  const monthsNeeded = projectedMonthsToTarget(goal);
  const targetMonths = goal.targetMonths ?? Math.round(target / Math.max(1, monthlyBase));

  return (
    <button
      onClick={onEdit}
      type="button"
      className="w-full cursor-pointer rounded-[var(--radius-3xl)] border border-border bg-surface shadow-[var(--shadow-1)] p-4 text-left transition-colors hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-900 motion-reduce:transition-none"
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid size-11 shrink-0 place-items-center rounded-lg bg-success-surface text-success-text">
            <ShieldIcon size={20} weight="bold" />
          </div>
          <div className="min-w-0">
            <div className="text-base leading-tight font-semibold text-text-primary">{goal.name}</div>
            <div className="mt-0.5 text-2xs text-text-secondary">
              {targetMonths} meses de despesas · fundação financeira
            </div>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-sans text-2xl leading-none font-bold tracking-tight text-success-text tabular-nums sm:text-3xl">
            {monthsCovered.toFixed(1)}
          </div>
          <div className="mt-1 font-mono text-2xs text-text-tertiary">de {targetMonths} meses</div>
        </div>
      </div>

      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-divider">
        <div className="h-full rounded-full bg-success" style={{ width: `${Math.min(100, p)}%` }} />
      </div>

      <div className="mt-2 flex items-center justify-between text-2xs">
        <div className="font-mono text-text-tertiary tabular-nums">{p}% concluído</div>
        <div className="font-mono text-text-secondary tabular-nums">
          faltam <span className="font-medium text-text-primary">{formatCurrency(Math.max(0, target - saved))}</span>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 border-t border-divider pt-3 text-2xs">
        <div className="flex items-center gap-1.5 text-text-secondary">
          <CalendarDotsIcon size={11} weight="bold" />
          <span>
            Média mensal:{' '}
            <span className="font-mono font-medium text-text-primary">{formatCurrency(monthlyBase)}/mês</span>
            <span className="text-text-tertiary">
              {averageMonthlyExpense > 0 ? ' · últimos 6 meses' : ' · do Orçamento'}
            </span>
          </span>
        </div>
        <span className="font-medium text-success-text">
          {status === 'complete'
            ? 'Reserva completa ✓'
            : monthsNeeded === Infinity
              ? 'sem aportes'
              : `~${monthsNeeded} ${monthsNeeded === 1 ? 'mês' : 'meses'}`}
        </span>
      </div>
    </button>
  );
}
